/**
 * ErectorFieldCoating — CR-2026-ERECTOR-E4 (2026-10-13). AISC 207-25 §5.12.3 field
 * preparation and §5.12.4 field application (replace §1.12.3 / §1.12.4 for erectors).
 * Server: src/routes/erectorFieldCoating.js. Procedures are approved by a QC approver
 * other than the author and then locked; field records need both procedures approved,
 * refuse ACCEPTED below the dew-point margin or outside DFT spec, and are never edited.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, MenuItem, TextField,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import PageGuide from "../../components/PageGuide";
import ApiCall from "../../utils/apicall";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { apiMessage } from "../../utils/erectionPlansApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const PTYPES = { FIELD_PREP: "Field preparation (§5.12.3)", FIELD_APPLICATION: "Field application (§5.12.4)" };
const REASONS = { FIELD_WELD: "Field weld", BOLTED_CONNECTION: "Bolted connection", DAMAGE: "Damage", FULL_FIELD_COAT: "Full field coat", OTHER: "Other" };
const EMPTY_P = { procedure_type: "FIELD_PREP", title: "", doc_reference: "", coating_mfr_reference: "", prep_methods: "", min_surface_over_dew_f: "5", body: "" };
const EMPTY_R = { area: "", reason: "FIELD_WELD", prep_method: "", product: "", batch: "", applicator: "", applicator_cert: "",
  ambient_temp_f: "", surface_temp_f: "", relative_humidity_pct: "", dew_point_f: "", dft_spec_min: "", dft_spec_max: "", dft_measured: "",
  result: "ACCEPTED", reject_reason: "" };

export default function ErectorFieldCoating() {
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: perms = [], id: myId } = loginUser?.decodedToken ?? {};
  const canEdit = hasPermission(perms, PERMISSIONS.EDIT_ERECTOR_COATING_PROCEDURE);
  const canApprove = hasPermission(perms, PERMISSIONS.APPROVE_ERECTOR_COATING_PROCEDURE);
  const canRecord = hasPermission(perms, PERMISSIONS.RECORD_FIELD_COATING);
  const canVoid = hasPermission(perms, PERMISSIONS.VOID_FIELD_ERECTION);

  const [procs, setProcs] = useState([]);
  const [pform, setPform] = useState(null); // null = closed; { id?, ...fields }
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [rows, setRows] = useState([]);
  const [rform, setRform] = useState(EMPTY_R);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const approved = (t) => procs.find((p) => p.procedure_type === t && p.status === "APPROVED");
  const ready = approved("FIELD_PREP") && approved("FIELD_APPLICATION");
  const margin = approved("FIELD_APPLICATION")?.min_surface_over_dew_f;

  const loadProcs = useCallback(async () => {
    try { const { data } = await ApiCall.get("/erector-coating/procedures"); setProcs(data?.data?.procedures || []); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Could not load procedures.") }); }
  }, []);
  const loadRows = useCallback(async () => {
    if (!jobId) { setRows([]); return; }
    try { const { data } = await ApiCall.get(`/erector-coating/records?job_id=${jobId}`); setRows(data?.data?.records || []); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Could not load records.") }); }
  }, [jobId]);
  useEffect(() => { loadProcs(); ApiCall.get("/job").then((r) => setJobs(r?.data?.data?.jobs || [])).catch(() => {}); }, [loadProcs]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const setP = (k) => (e) => setPform((f) => ({ ...f, [k]: e.target.value }));
  const setR = (k) => (e) => setRform((f) => ({ ...f, [k]: e.target.value }));

  async function saveProc() {
    setBusy(true); setMsg(null);
    const body = { ...pform, prep_methods: String(pform.prep_methods || "").split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (pform.id) await ApiCall.patch(`/erector-coating/procedures/${pform.id}`, body);
      else await ApiCall.post("/erector-coating/procedures", body);
      setPform(null); setMsg({ severity: "success", text: "Draft saved. A QC approver other than you must approve it." });
      await loadProcs();
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Procedure not saved.") }); }
    finally { setBusy(false); }
  }
  async function approve(p) {
    try { await ApiCall.post(`/erector-coating/procedures/${p.id}/approve`); await loadProcs(); setMsg({ severity: "success", text: `Rev ${p.revision} approved.` }); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not approved.") }); }
  }
  async function submitRecord() {
    setBusy(true); setMsg(null);
    try {
      const { data } = await ApiCall.post("/erector-coating/records", { ...rform, job_id: Number(jobId),
        reject_reason: rform.result === "REJECTED" ? rform.reject_reason : undefined });
      setMsg({ severity: data?.reminder ? "warning" : "success", text: data?.reminder || "Recorded." });
      setRform(EMPTY_R); await loadRows();
    } catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not recorded.") }); }
    finally { setBusy(false); }
  }
  async function voidRecord(r) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(`Void the coating record for ${r.area}? Give the reason:`);
    if (!reason || !reason.trim()) return;
    try { await ApiCall.post(`/erector-coating/records/${r.id}/void`, { reason }); await loadRows(); }
    catch (e) { setMsg({ severity: "error", text: apiMessage(e, "Not voided.") }); }
  }

  const surf = Number(rform.surface_temp_f); const dew = Number(rform.dew_point_f);
  const condWarn = rform.surface_temp_f !== "" && rform.dew_point_f !== "" && margin !== undefined && surf < dew + Number(margin);

  return (
    <>
      <Helmet><title>Field Coating (Erector) | SFM</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>Field Coating (Erector)</Typography>
        <PageGuide
          why="When the erector coats or touches up in the field, AISC 207-25 §5.12.3 and §5.12.4 replace the shop coating clauses."
          who="QM or PM write the field procedures; a different QM or Admin approves them. Foreman and field crews record each field coat."
          what="Approved field prep and application procedures, and an append-only record of each field coat with conditions and DFT."
          how="Approve both procedures first. Then record the area, prep, product, applicator, conditions and DFT. Out-of-limit work is recorded as rejected."
          when="Before any field coating starts, and each time field coating or touch-up is done."
          standard="AISC 207-25 §5.12.3 · §5.12.4 · SSPC surface prep · coating manufacturer data sheet"
        />
        {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6">Field procedures</Typography>
            {canEdit && !pform && <Button variant="outlined" onClick={() => setPform({ ...EMPTY_P })}>New procedure revision</Button>}
          </Stack>
          {pform && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField select size="small" label="Type" value={pform.procedure_type} onChange={setP("procedure_type")} disabled={!!pform.id} sx={{ minWidth: 240 }}>
                  {Object.entries(PTYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Title *" value={pform.title} onChange={setP("title")} sx={{ minWidth: 260 }} />
                <TextField size="small" label="Document no." value={pform.doc_reference || ""} onChange={setP("doc_reference")} />
                <TextField size="small" label="Coating mfr data sheet" value={pform.coating_mfr_reference || ""} onChange={setP("coating_mfr_reference")} />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField size="small" label="Prep methods (comma separated, e.g. SSPC-SP2, SSPC-SP3)" value={pform.prep_methods} onChange={setP("prep_methods")} sx={{ minWidth: 360 }} />
                <TextField size="small" label="Surface over dew point (°F)" value={pform.min_surface_over_dew_f} onChange={setP("min_surface_over_dew_f")} />
              </Stack>
              <TextField size="small" multiline minRows={3} label="Procedure text" value={pform.body || ""} onChange={setP("body")} />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy || !String(pform.title).trim()} onClick={saveProc}>Save draft</Button>
                <Button onClick={() => setPform(null)}>Cancel</Button>
              </Stack>
            </Stack>
          )}
          <SmartTable tableId="field-coating-procedures" lockedColumns={["Procedure", "Rev", "Status"]} size="small">
            <TableHead><TableRow><TableCell>Procedure</TableCell><TableCell>Rev</TableCell><TableCell>Title</TableCell><TableCell>Margin</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {procs.length === 0 && <TableRow><TableCell colSpan={6}>No field procedures yet. Both must be approved before field coating is recorded.</TableCell></TableRow>}
              {procs.map((p) => (
                <TableRow key={p.id} sx={p.status === "SUPERSEDED" ? { opacity: 0.5 } : undefined}>
                  <TableCell>{PTYPES[p.procedure_type]}</TableCell><TableCell>{p.revision}</TableCell>
                  <TableCell>{p.title}{p.prep_methods?.length ? <Typography variant="caption" display="block">{p.prep_methods.join(", ")}</Typography> : null}</TableCell>
                  <TableCell>{p.min_surface_over_dew_f} °F</TableCell>
                  <TableCell><Chip size="small" color={p.status === "APPROVED" ? "success" : p.status === "DRAFT" ? "warning" : "default"} label={p.status.toLowerCase()} /></TableCell>
                  <TableCell>
                    {p.status === "DRAFT" && canEdit && <Button size="small" onClick={() => setPform({ ...p, prep_methods: (p.prep_methods || []).join(", ") })}>Edit</Button>}
                    {p.status === "DRAFT" && canApprove && Number(p.created_by) !== Number(myId) && <Button size="small" onClick={() => approve(p)}>Approve</Button>}
                    {p.status === "DRAFT" && canApprove && Number(p.created_by) === Number(myId) && <Typography variant="caption">Another approver needed</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Field coating records</Typography>
          <TextField select size="small" label="Job" value={jobId} onChange={(e) => setJobId(e.target.value)} sx={{ minWidth: 300, mb: 2 }}>
            <MenuItem value="">Select a job…</MenuItem>
            {jobs.map((j) => <MenuItem key={j.id} value={j.id}>{j.name}</MenuItem>)}
          </TextField>
          {jobId && canRecord && !ready && <Alert severity="warning" sx={{ mb: 2 }}>Both field procedures must be approved before field coating can be recorded.</Alert>}
          {jobId && canRecord && ready && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField size="small" label="Area / piece / connection *" value={rform.area} onChange={setR("area")} sx={{ minWidth: 240 }} />
                <TextField select size="small" label="Reason" value={rform.reason} onChange={setR("reason")} sx={{ minWidth: 180 }}>
                  {Object.entries(REASONS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Prep method *" value={rform.prep_method} onChange={setR("prep_method")} sx={{ minWidth: 180 }}>
                  {(approved("FIELD_PREP")?.prep_methods?.length ? approved("FIELD_PREP").prep_methods : ["SSPC-SP2", "SSPC-SP3", "SSPC-SP11"]).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Product *" value={rform.product} onChange={setR("product")} />
                <TextField size="small" label="Batch" value={rform.batch} onChange={setR("batch")} />
                <TextField size="small" label="Applicator *" value={rform.applicator} onChange={setR("applicator")} />
                <TextField size="small" label="Applicator cert" value={rform.applicator_cert} onChange={setR("applicator_cert")} />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                <TextField size="small" label="Ambient °F *" value={rform.ambient_temp_f} onChange={setR("ambient_temp_f")} />
                <TextField size="small" label="Surface °F *" value={rform.surface_temp_f} onChange={setR("surface_temp_f")} />
                <TextField size="small" label="RH % *" value={rform.relative_humidity_pct} onChange={setR("relative_humidity_pct")} />
                <TextField size="small" label="Dew point °F *" value={rform.dew_point_f} onChange={setR("dew_point_f")} />
                <TextField size="small" label="DFT min" value={rform.dft_spec_min} onChange={setR("dft_spec_min")} />
                <TextField size="small" label="DFT max" value={rform.dft_spec_max} onChange={setR("dft_spec_max")} />
                <TextField size="small" label="DFT measured" value={rform.dft_measured} onChange={setR("dft_measured")} />
                <TextField select size="small" label="Result" value={rform.result} onChange={setR("result")} sx={{ minWidth: 140 }}>
                  <MenuItem value="ACCEPTED">Accepted</MenuItem><MenuItem value="REJECTED">Rejected</MenuItem>
                </TextField>
              </Stack>
              {condWarn && <Alert severity="error">Surface is less than {margin} °F above the dew point. This cannot be accepted; record it as rejected with the reason.</Alert>}
              {rform.result === "REJECTED" && <TextField size="small" label="Reject reason *" value={rform.reject_reason} onChange={setR("reject_reason")} />}
              <Button variant="contained" sx={{ alignSelf: "flex-start" }} disabled={busy || !rform.area.trim() || !rform.prep_method} onClick={submitRecord}>Record</Button>
            </Stack>
          )}
          <TableContainer>
            <SmartTable tableId="field-coating-records" lockedColumns={["When", "DFT", "Result"]} size="small">
              <TableHead><TableRow><TableCell>When</TableCell><TableCell>Area</TableCell><TableCell>Reason</TableCell><TableCell>Prep / product</TableCell>
                <TableCell>Conditions</TableCell><TableCell>DFT</TableCell><TableCell>Result</TableCell><TableCell>Proc revs</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {jobId && rows.length === 0 && <TableRow><TableCell colSpan={9}>No field coating recorded for this job.</TableCell></TableRow>}
                {rows.map((r) => (
                  <TableRow key={r.id} sx={r.voided ? { opacity: 0.5 } : undefined}>
                    <TableCell>{new Date(r.recorded_at).toLocaleString()}</TableCell>
                    <TableCell>{r.area}</TableCell><TableCell>{REASONS[r.reason]}</TableCell>
                    <TableCell>{r.prep_method} · {r.product}{r.batch ? ` (${r.batch})` : ""}<Typography variant="caption" display="block">{r.applicator}</Typography></TableCell>
                    <TableCell>{`surf ${Number(r.surface_temp_f)} / dew ${Number(r.dew_point_f)} °F`}{r.conditions_ok ? "" : " (out of limits)"}</TableCell>
                    <TableCell>{r.dft_measured !== null ? `${Number(r.dft_measured)} (${Number(r.dft_spec_min)} to ${Number(r.dft_spec_max)})` : "—"}</TableCell>
                    <TableCell>{r.voided ? <Chip size="small" label={`Voided: ${r.void_reason}`} />
                      : <Chip size="small" color={r.result === "REJECTED" ? "error" : "success"} label={r.result.toLowerCase()} />}{r.reject_reason ? ` ${r.reject_reason}` : ""}</TableCell>
                    <TableCell>{`prep ${r.prep_revision} · app ${r.app_revision}`}</TableCell>
                    <TableCell>{canVoid && !r.voided && <Button size="small" onClick={() => voidRecord(r)}>Void</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
          </TableContainer>
        </Paper>
      </Container>
    </>
  );
}
